import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { db } from "firebaseDb";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";

function StockLocations({ orchards }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [locationName, setLocationName] = useState("");
  const [selectedOrchardCodes, setSelectedOrchardCodes] = useState([]);
  console.log(orchards);
  const orchardOptions = useMemo(
    () =>
      [...orchards]
        .map((orchard) => ({
          siteCode: orchard.siteCode || "",
          siteName: orchard.siteName || "",
        }))
        .filter((orchard) => orchard.siteCode)
        .filter((orchard) => orchard.archive !== true)
        .sort((a, b) => a.siteCode.localeCompare(b.siteCode)),
    [orchards]
  );

  const loadMappings = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(
        query(collection(db, "StocktakeLocations"), orderBy("name", "asc"))
      );
      setMappings(
        snapshot.docs.map((item) => ({
          key: item.id,
          ...item.data(),
          orchardSiteCodes: Array.isArray(item.data()?.orchardSiteCodes)
            ? item.data().orchardSiteCodes
            : [],
        }))
      );
    } catch (error) {
      console.error("Failed loading stocktake locations", error);
      setMappings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const resetForm = () => {
    setEditingKey("");
    setLocationName("");
    setSelectedOrchardCodes([]);
  };

  const toggleOrchardSelection = (siteCode) => {
    setSelectedOrchardCodes((prev) =>
      prev.includes(siteCode) ? prev.filter((code) => code !== siteCode) : [...prev, siteCode]
    );
  };

  const saveMapping = async () => {
    const trimmedName = locationName.trim();
    if (!trimmedName) {
      alert("Please enter a stock location name.");
      return;
    }

    if (!selectedOrchardCodes.length) {
      alert("Please select at least one orchard.");
      return;
    }

    const payload = {
      name: trimmedName,
      orchardSiteCodes: [...selectedOrchardCodes].sort((a, b) => a.localeCompare(b)),
      updatedAt: new Date(),
    };

    setSaving(true);
    try {
      if (editingKey) {
        await setDoc(doc(db, "StocktakeLocations", editingKey), payload, { merge: true });
      } else {
        await addDoc(collection(db, "StocktakeLocations"), {
          ...payload,
          createdAt: new Date(),
        });
      }
      await loadMappings();
      resetForm();
      alert("Stock location saved.");
    } catch (error) {
      console.error("Failed saving stock location", error);
      alert("Failed to save stock location.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (mapping) => {
    setEditingKey(mapping.key);
    setLocationName(mapping.name || "");
    setSelectedOrchardCodes(mapping.orchardSiteCodes || []);
  };

  const removeMapping = async (mappingKey) => {
    if (!window.confirm("Delete this stock location mapping?")) return;
    try {
      await deleteDoc(doc(db, "StocktakeLocations", mappingKey));
      if (editingKey === mappingKey) {
        resetForm();
      }
      await loadMappings();
    } catch (error) {
      console.error("Failed deleting stock location", error);
      alert("Failed to delete stock location.");
    }
  };

  return (
    <MDBox>
      <MDTypography variant="h6" fontWeight="medium" mb={2}>
        Stocktake Location Mapping
      </MDTypography>
      <Card sx={{ p: 2, mb: 2 }}>
        <MDBox display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2} mb={2}>
          <TextField
            fullWidth
            label="Stock Location Name"
            value={locationName}
            onChange={(event) => setLocationName(event.target.value)}
          />
        </MDBox>
        <MDTypography variant="button" color="text" mb={1} display="block">
          Include orchard sprays in this location&apos;s used stock
        </MDTypography>
        <MDBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "0.5rem",
            p: 1,
          }}
        >
          {orchardOptions.map((orchard) => (
            <FormControlLabel
              key={orchard.siteCode}
              control={
                <Checkbox
                  checked={selectedOrchardCodes.includes(orchard.siteCode)}
                  onChange={() => toggleOrchardSelection(orchard.siteCode)}
                />
              }
              label={`${orchard.siteCode} - ${orchard.siteName}`}
            />
          ))}
        </MDBox>
        <MDBox mt={2} display="flex" gap={1}>
          <MDButton variant="contained" color="success" disabled={saving} onClick={saveMapping}>
            <Icon>{editingKey ? "save" : "add"}</Icon>&nbsp;
            {editingKey ? "Update Location" : "Add Location"}
          </MDButton>
          {editingKey && (
            <MDButton variant="outlined" color="secondary" onClick={resetForm}>
              Cancel Edit
            </MDButton>
          )}
        </MDBox>
      </Card>

      {loading ? (
        <MDBox textAlign="center" py={4}>
          <CircularProgress />
        </MDBox>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Orchards</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={mapping.key} hover>
                  <TableCell>{mapping.name}</TableCell>
                  <TableCell>{(mapping.orchardSiteCodes || []).join(", ")}</TableCell>
                  <TableCell align="right">
                    <MDButton variant="text" color="dark" onClick={() => startEdit(mapping)}>
                      <Icon>edit</Icon>&nbsp;edit
                    </MDButton>
                    <MDButton
                      variant="text"
                      color="error"
                      onClick={() => removeMapping(mapping.key)}
                    >
                      <Icon>delete</Icon>&nbsp;delete
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}
              {mappings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No stock locations configured.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </MDBox>
  );
}

StockLocations.propTypes = {
  orchards: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default StockLocations;
